import React, { useEffect, useState } from "react";
import {
  Button,
  Tag,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Popconfirm,
  Card,
  Tooltip,
  Tabs,
} from "antd";
import Table from 'components/Common/Table';
import {
  PlusOutlined,
  SyncOutlined,
  ApiOutlined,
  DeleteOutlined,
  EyeOutlined,
  EditOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  AppstoreOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import * as tmActions from "store/actions/TenantManagementActions";

const SaasProducts = () => {
  const navigate = useNavigate();

  // Products state
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [savingProduct, setSavingProduct] = useState(false);
  const [productForm] = Form.useForm();

  // Connections state
  const [connections, setConnections] = useState([]);
  const [connectionsLoading, setConnectionsLoading] = useState(false);
  const [connectionModalOpen, setConnectionModalOpen] = useState(false);
  const [editingConnection, setEditingConnection] = useState(null);
  const [savingConnection, setSavingConnection] = useState(false);
  const [connectionForm] = Form.useForm();

  const fetchProducts = async () => {
    setProductsLoading(true);
    const data = await tmActions.loadProducts();
    if (data) setProducts(Array.isArray(data) ? data : data.results || []);
    setProductsLoading(false);
  };

  const fetchConnections = async () => {
    setConnectionsLoading(true);
    const data = await tmActions.loadAppConnections();
    if (data) setConnections(Array.isArray(data) ? data : data.results || []);
    setConnectionsLoading(false);
  };

  useEffect(() => {
    fetchProducts();
    fetchConnections();
  }, []);

  // ---- Product handlers ----
  const handleAddProduct = () => {
    setEditingProduct(null);
    productForm.resetFields();
    productForm.setFieldsValue({ is_ship_to_enabled: true });
    setProductModalOpen(true);
  };

  const handleEditProduct = (record) => {
    setEditingProduct(record);
    productForm.setFieldsValue({
      name: record.name,
      code: record.code,
      frontend_url: record.frontend_url,
      backend_url: record.backend_url,
      default_hsn: record.default_hsn,
      is_ship_to_enabled: record.is_ship_to_enabled,
      invoice_terms: record.invoice_terms,
    });
    setProductModalOpen(true);
  };

  const handleSaveProduct = async () => {
    try {
      const values = await productForm.validateFields();
      setSavingProduct(true);
      let result;
      if (editingProduct) {
        result = await tmActions.updateProduct(editingProduct.id, values);
      } else {
        result = await tmActions.createProduct(values);
      }
      if (result) {
        setProductModalOpen(false);
        productForm.resetFields();
        fetchProducts();
      }
      setSavingProduct(false);
    } catch {
      setSavingProduct(false);
    }
  };

  const handleDeleteProduct = async (id) => {
    const result = await tmActions.deleteProduct(id);
    if (result) fetchProducts();
  };

  // ---- Connection handlers ----
  const handleAddConnection = () => {
    setEditingConnection(null);
    connectionForm.resetFields();
    connectionForm.setFieldsValue({
      tenants_endpoint: "/tenant/tenants/",
      subscriptions_endpoint: "/tenant/subscriptions/",
    });
    setConnectionModalOpen(true);
  };

  const handleEditConnection = (record) => {
    setEditingConnection(record);
    connectionForm.setFieldsValue({
      product: record.product,
      tenants_endpoint: record.tenants_endpoint,
      subscriptions_endpoint: record.subscriptions_endpoint,
    });
    setConnectionModalOpen(true);
  };

  const handleSaveConnection = async () => {
    try {
      const values = await connectionForm.validateFields();
      setSavingConnection(true);
      let result;
      if (editingConnection) {
        result = await tmActions.updateAppConnection(editingConnection.id, values);
      } else {
        result = await tmActions.createAppConnection(values);
      }
      if (result) {
        setConnectionModalOpen(false);
        connectionForm.resetFields();
        fetchConnections();
      }
      setSavingConnection(false);
    } catch {
      setSavingConnection(false);
    }
  };

  const handleDeleteConnection = async (id) => {
    const result = await tmActions.deleteAppConnection(id);
    if (result) fetchConnections();
  };

  const handleTestConnection = async (id) => {
    const result = await tmActions.testConnection(id);
    if (result) fetchConnections();
  };

  const handleSync = async (id) => {
    const result = await tmActions.syncAll(id);
    if (result) fetchConnections();
  };

  // Check which products have connections
  const connectedProductIds = connections.map((c) => c.product);
  const availableProducts = editingConnection
    ? products
    : products.filter((p) => !connectedProductIds.includes(p.id));

  // ---- Product columns ----
  const productColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      render: (text) => <code>{text}</code>,
    },
    {
      title: "Frontend URL",
      dataIndex: "frontend_url",
      key: "frontend_url",
      ellipsis: true,
    },
    {
      title: "Backend URL",
      dataIndex: "backend_url",
      key: "backend_url",
      ellipsis: true,
    },
    {
      title: "Connection",
      key: "connection",
      render: (_, record) =>
        connectedProductIds.includes(record.id) ? (
          <Tag icon={<CheckCircleOutlined />} color="success">Connected</Tag>
        ) : (
          <Tag color="default">Not Connected</Tag>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit Product">
            <Button size="small" icon={<EditOutlined />} onClick={() => handleEditProduct(record)} />
          </Tooltip>
          {!connectedProductIds.includes(record.id) && (
            <Popconfirm title="Delete this product?" onConfirm={() => handleDeleteProduct(record.id)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // ---- Connection columns ----
  const connectionColumns = [
    {
      title: "Product",
      dataIndex: "product_name",
      key: "product_name",
      render: (text, record) => (
        <a onClick={() => navigate(`/tenant-management/saas-products/${record.id}`)}>{text}</a>
      ),
    },
    {
      title: "Code",
      dataIndex: "product_code",
      key: "product_code",
      render: (text) => <code>{text}</code>,
    },
    {
      title: "Backend URL",
      dataIndex: "backend_url",
      key: "backend_url",
      ellipsis: true,
    },
    {
      title: "Status",
      dataIndex: "is_connected",
      key: "is_connected",
      render: (connected) =>
        connected ? (
          <Tag icon={<CheckCircleOutlined />} color="success">Connected</Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="error">Disconnected</Tag>
        ),
    },
    {
      title: "Tenants",
      dataIndex: "tenant_count",
      key: "tenant_count",
      align: "center",
    },
    {
      title: "Subscriptions",
      dataIndex: "subscription_count",
      key: "subscription_count",
      align: "center",
    },
    {
      title: "Sync",
      dataIndex: "sync_status",
      key: "sync_status",
      render: (s) => {
        const colors = { idle: "default", syncing: "processing", error: "error" };
        return <Tag color={colors[s] || "default"}>{s}</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/tenant-management/saas-products/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title="Test Connection">
            <Button size="small" icon={<ApiOutlined />} onClick={() => handleTestConnection(record.id)} />
          </Tooltip>
          <Tooltip title="Sync All">
            <Button
              size="small"
              icon={<SyncOutlined />}
              onClick={() => handleSync(record.id)}
              loading={record.sync_status === "syncing"}
            />
          </Tooltip>
          <Tooltip title="Edit Connection">
            <Button size="small" icon={<EditOutlined />} onClick={() => handleEditConnection(record)} />
          </Tooltip>
          <Popconfirm title="Delete this connection?" onConfirm={() => handleDeleteConnection(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const tabItems = [
    {
      key: "products",
      label: (
        <span><AppstoreOutlined /> Products ({products.length})</span>
      ),
      children: (
        <>
          <div style={{ marginBottom: 16, display: "flex", justifyContent: "flex-end" }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddProduct}>
              Add Product
            </Button>
          </div>
          <Table
            dataSource={products}
            columns={productColumns}
            rowKey="id"
            loading={productsLoading}
            pagination={{ defaultPageSize: 20, showSizeChanger: true }}
          />
        </>
      ),
    },
    {
      key: "connections",
      label: (
        <span><LinkOutlined /> App Connections ({connections.length})</span>
      ),
      children: (
        <>
          <div style={{ marginBottom: 16, display: "flex", justifyContent: "flex-end" }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddConnection}>
              Add Connection
            </Button>
          </div>
          <Table
            dataSource={connections}
            columns={connectionColumns}
            rowKey="id"
            loading={connectionsLoading}
            pagination={{ defaultPageSize: 20, showSizeChanger: true }}
          />
        </>
      ),
    },
  ];

  return (
    <div style={{ padding: "16px" }}>
      <Card title="SaaS Products">
        <Tabs items={tabItems} />
      </Card>

      {/* Add/Edit Product Modal */}
      <Modal
        title={editingProduct ? "Edit Product" : "Add Product"}
        open={productModalOpen}
        onOk={handleSaveProduct}
        onCancel={() => setProductModalOpen(false)}
        confirmLoading={savingProduct}
        width={600}
      >
        <Form form={productForm} layout="vertical">
          <Form.Item
            name="name"
            label="Product Name"
            rules={[{ required: true, message: "Please enter product name" }]}
          >
            <Input placeholder="e.g., ERPTeson" />
          </Form.Item>
          <Form.Item
            name="code"
            label="Product Code"
            rules={[{ required: true, message: "Please enter product code" }]}
            extra="Unique identifier. e.g., ERPTESON"
          >
            <Input placeholder="e.g., ERPTESON" disabled={!!editingProduct} />
          </Form.Item>
          <Form.Item
            name="frontend_url"
            label="Frontend URL"
            rules={[
              { required: true, message: "Please enter frontend URL" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  try {
                    new URL(value);
                    return Promise.resolve();
                  } catch {
                    return Promise.reject(new Error("Please enter a valid URL"));
                  }
                },
              },
            ]}
          >
            <Input placeholder="https://erp.teson.in" />
          </Form.Item>
          <Form.Item
            name="backend_url"
            label="Backend URL"
            rules={[
              { required: true, message: "Please enter backend URL" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  try {
                    new URL(value);
                    return Promise.resolve();
                  } catch {
                    return Promise.reject(new Error("Please enter a valid URL"));
                  }
                },
              },
            ]}
          >
            <Input placeholder="https://api.erp.teson.in" />
          </Form.Item>
          <Form.Item name="default_hsn" label="Default HSN Code">
            <Input placeholder="e.g., 998314" />
          </Form.Item>
          <Form.Item name="is_ship_to_enabled" label="Ship To Enabled" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="invoice_terms" label="Invoice Terms (HTML)">
            <Input.TextArea rows={3} placeholder="Default invoice terms..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Add/Edit Connection Modal */}
      <Modal
        title={editingConnection ? "Edit Product Connection" : "Add Product Connection"}
        open={connectionModalOpen}
        onOk={handleSaveConnection}
        onCancel={() => setConnectionModalOpen(false)}
        confirmLoading={savingConnection}
        width={520}
      >
        <Form form={connectionForm} layout="vertical">
          <Form.Item
            name="product"
            label="Product"
            rules={[{ required: true, message: "Please select a product" }]}
          >
            <Select
              placeholder="Select a product"
              disabled={!!editingConnection}
              showSearch
              optionFilterProp="label"
              options={availableProducts.map((p) => ({
                value: p.id,
                label: `${p.name} (${p.code})`,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="api_token"
            label="API Token"
            rules={editingConnection ? [] : [{ required: true, message: "Please enter the API token" }]}
            extra="Generate an API token from the app's admin panel (tcrm_...)"
          >
            <Input.Password placeholder={editingConnection ? "Leave blank to keep current" : "tcrm_..."} />
          </Form.Item>
          <Form.Item
            name="tenants_endpoint"
            label="Tenants Endpoint"
            rules={[{ required: true, message: "Please enter tenants endpoint" }]}
            extra="API path for listing tenants on the remote app"
          >
            <Input placeholder="/tenant/tenants/" />
          </Form.Item>
          <Form.Item
            name="subscriptions_endpoint"
            label="Subscriptions Endpoint"
            rules={[{ required: true, message: "Please enter subscriptions endpoint" }]}
            extra="API path for listing subscriptions on the remote app"
          >
            <Input placeholder="/tenant/subscriptions/" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SaasProducts;
